import React, { useState } from 'react';
import useNotificaciones from '../../hooks/useNotificaciones';
import Skeleton from '../Skeleton';
import './NotificationCenter.css';

/**
 * Centro de Notificaciones
 * Componente de ejemplo para mostrar notificaciones del usuario
 */
const NotificationCenter = ({ isOpen, onClose }) => {
  const {
    notificaciones,
    noLeidas,
    loading,
    marcarComoLeida,
    marcarTodasComoLeidas,
    eliminarNotificacion
  } = useNotificaciones();

  const [filter, setFilter] = useState('todas'); // 'todas', 'noLeidas'

  if (!isOpen) return null;

  const notificacionesFiltradas = filter === 'noLeidas'
    ? notificaciones.filter(n => !n.leida)
    : notificaciones;

  const handleMarcarComoLeida = async (id) => {
    await marcarComoLeida(id);
  };

  const handleMarcarTodasComoLeidas = async () => {
    await marcarTodasComoLeidas();
  };

  const handleEliminar = async (id) => {
    await eliminarNotificacion(id);
  };

  const getIconoTipo = (tipo) => {
    const iconos = {
      sesion_asignada: '✅',
      sesion_cancelada: '🔴',
      pago_confirmado: '💰',
      suscripcion_por_vencer: '⏰',
      planificacion_actualizada: '📋',
      general: 'ℹ️'
    };
    return iconos[tipo] || 'ℹ️';
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    const ahora = new Date();
    const diff = ahora - date;
    const minutos = Math.floor(diff / 60000);
    const horas = Math.floor(diff / 3600000);
    const dias = Math.floor(diff / 86400000);

    if (minutos < 1) return 'Justo ahora';
    if (minutos < 60) return `Hace ${minutos} min`;
    if (horas < 24) return `Hace ${horas}h`;
    if (dias < 7) return `Hace ${dias}d`;
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="notification-center-overlay" onClick={onClose}>
      <div className="notification-center" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="notification-center-header">
          <div>
            <h3>Notificaciones</h3>
            {noLeidas > 0 && (
              <span className="notification-badge">{noLeidas}</span>
            )}
          </div>
          <button className="notification-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Filtros */}
        <div className="notification-filters">
          <button
            className={`filter-btn ${filter === 'todas' ? 'active' : ''}`}
            onClick={() => setFilter('todas')}
          >
            Todas
          </button>
          <button
            className={`filter-btn ${filter === 'noLeidas' ? 'active' : ''}`}
            onClick={() => setFilter('noLeidas')}
          >
            No leídas ({noLeidas})
          </button>
          {noLeidas > 0 && (
            <button
              className="mark-all-btn"
              onClick={handleMarcarTodasComoLeidas}
            >
              Marcar todas como leídas
            </button>
          )}
        </div>

        {/* Lista de notificaciones */}
        <div className="notification-list">
          {loading ? (
            // Skeleton loader mientras carga
            Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="notification-skeleton">
                <Skeleton variant="circular" width="40px" height="40px" />
                <div style={{ flex: 1 }}>
                  <Skeleton variant="text" width="80%" height="18px" />
                  <Skeleton variant="text" width="60%" height="14px" />
                </div>
              </div>
            ))
          ) : notificacionesFiltradas.length === 0 ? (
            <div className="notification-empty">
              <p>📭</p>
              <p>No tienes notificaciones</p>
            </div>
          ) : (
            notificacionesFiltradas.map((notif) => (
              <div
                key={notif._id}
                className={`notification-item ${!notif.leida ? 'unread' : ''}`}
                onClick={() => !notif.leida && handleMarcarComoLeida(notif._id)}
              >
                <div className="notification-icon">
                  {getIconoTipo(notif.tipo)}
                </div>
                <div className="notification-content">
                  <h4 className="notification-title">{notif.titulo}</h4>
                  <p className="notification-message">{notif.mensaje}</p>
                  <span className="notification-time">
                    {formatearFecha(notif.fechaCreacion)}
                  </span>
                </div>
                <div className="notification-actions">
                  {!notif.leida && (
                    <span className="notification-unread-dot" title="No leída"></span>
                  )}
                  <button
                    className="notification-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEliminar(notif._id);
                    }}
                    title="Eliminar"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
